import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EllipsisVerticalIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Button, Divider, Input, Small } from "@fronterahealth/frontera-ui-components";

interface OptionItem {
  id: string;
  option: string;
}

interface OptionsEditorProps {
  options: string[];
  onChange: (options: string[]) => void;
  readonly?: boolean;
}

interface SortableItemProps {
  id: string;
  index: number;
  option: string; // This should be the string value, not the OptionItem
  onRemove: (index: number) => void;
  onUpdate: (index: number, value: string) => void;
  readonly?: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, index, option, onRemove, onUpdate, readonly = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: readonly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center space-x-2 bg-white border rounded-md px-2 group">
      {!readonly && (
        <div {...attributes} {...listeners} className="p-2 text-text-secondary rounded-full cursor-grab">
          <div className="flex w-6">
            <EllipsisVerticalIcon className="h-6 w-6" />
            <EllipsisVerticalIcon className="h-6 w-6 -ml-3" />
          </div>
        </div>
      )}
      <Input
        value={option}
        onChange={(e) => onUpdate(index, e.target.value)}
        className="grow [&>div>input]:ring-0 [&>div>input]:shadow-none -mb-6 max-w-none"
        placeholder={`Option ${index + 1}`}
        name={`option-${id}`}
        label=""
        disabled={readonly}
      />
      {!readonly && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 opacity-0 group-hover:opacity-100"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export const OptionsEditor: React.FC<OptionsEditorProps> = ({ options = [], onChange, readonly = false }) => {
  const [internalOptions, setInternalOptions] = useState<OptionItem[]>(
    options.map((option, index) => ({ id: `option-${index}`, option })),
  );
  const [inputValue, setInputValue] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  React.useEffect(() => {
    setInternalOptions(options.map((option, index) => ({ id: `option-${index}`, option })));
  }, [options]);

  const handleAddOption = () => {
    if (inputValue.trim() !== "") {
      const newOption: OptionItem = {
        id: `option-${Date.now()}`,
        option: inputValue.trim(),
      };
      const newOptions = [...internalOptions, newOption];
      setInternalOptions(newOptions);
      onChange(newOptions.map((item) => item.option));
      setInputValue("");
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...internalOptions];
    newOptions.splice(index, 1);
    setInternalOptions(newOptions);
    onChange(newOptions.map((item) => item.option));
  };

  const handleUpdateOption = (index: number, value: string) => {
    const newOptions = [...internalOptions];
    newOptions[index] = { ...newOptions[index], option: value };
    setInternalOptions(newOptions);
    onChange(newOptions.map((item) => item.option));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddOption();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = internalOptions.findIndex((item) => item.id === active.id);
      const newIndex = internalOptions.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOptions = arrayMove(internalOptions, oldIndex, newIndex);
        setInternalOptions(newOptions);
        onChange(newOptions.map((item) => item.option));
      }
    }
  };

  return (
    <div className="p-4 border outline-white rounded-xl bg-white mb-6">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={internalOptions.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {internalOptions.length === 0 ? (
              <div className="text-text-secondary text-center rounded-xl bg-limestone-50 p-4">
                <Small>
                  <em>No options configured. Add your first option below</em>
                </Small>
              </div>
            ) : (
              internalOptions.map((item, index) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  index={index}
                  option={item.option}
                  onRemove={handleRemoveOption}
                  onUpdate={handleUpdateOption}
                  readonly={readonly}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
      {!readonly && (
        <>
          <Divider className="mt-6"></Divider>
          <div className="flex items-center space-x-2 mt-4 w-full">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="grow -mb-6 max-w-none"
              placeholder="Add new option"
              name="new-option"
              label=""
            />

            <Button
              text={<PlusIcon className="h-6 w-6" />}
              buttonAction="regular"
              appearance={"primary"}
              onClick={handleAddOption}
              className="rounded-xs px-1! py-1!"
            />
          </div>
        </>
      )}
    </div>
  );
};
